import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { Plus, LogOut, Trash2, MoreHorizontal, FolderKanban, Sparkles, Pencil, UserCog, Shield, Users, Star, FolderInput, Inbox, ChevronDown, LayoutTemplate, LifeBuoy, Layers } from "lucide-react";
import UpgradeModal from "@/components/UpgradeModal";
import RenameProjectModal from "@/components/RenameProjectModal";
import ProfileModal from "@/components/ProfileModal";
import OnboardingModal from "@/components/OnboardingModal";
import FoldersSidebar, { DRAG_TYPE } from "@/components/FoldersSidebar";
import FolderModal from "@/components/FolderModal";
import TemplateGalleryModal from "@/components/TemplateGalleryModal";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import AiCreditBadge from "@/components/AiCreditBadge";
import usePageMeta from "@/hooks/usePageMeta";
import { useIsMobile } from "@/hooks/useMediaQuery";
import usePullToRefresh from "@/hooks/usePullToRefresh";
import useLongPress from "@/hooks/useLongPress";
import { confirmAction } from "@/lib/confirm";
import PullIndicator from "@/components/mobile/PullIndicator";
import InstallPrompt from "@/components/mobile/InstallPrompt";
import Fab from "@/components/mobile/Fab";
import ActionSheet, { ActionRow } from "@/components/mobile/ActionSheet";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuPortal,
} from "@/components/ui/dropdown-menu";
import BrandLogo from "@/components/BrandLogo";

const PROJECT_COLORS = ["#E05236", "#4A5D4E", "#D4A373", "#7D8C96", "#0A0A0A"];
const FREE_LIMIT = 1;

// Little Gantt glyph on a coloured tile: gives every project a recognisable "app icon".
function ProjectTile({ color, className = "h-12 w-12" }) {
  return (
    <div
      className={`relative shrink-0 overflow-hidden rounded-[14px] ${className}`}
      style={{ background: color }}
      aria-hidden
    >
      <svg viewBox="0 0 48 48" className="h-full w-full">
        <rect x="9" y="13" width="17" height="5.5" rx="1.6" fill="#fff" fillOpacity=".92" />
        <rect x="16" y="21.5" width="23" height="5.5" rx="1.6" fill="#fff" fillOpacity=".6" />
        <rect x="24" y="30" width="14" height="5.5" rx="1.6" fill="#fff" fillOpacity=".92" />
      </svg>
    </div>
  );
}

// Same menu on every screen size: a dropdown on desktop, an action sheet on phones.
function ProjectMenu({ p, folders, t, onToggleStar, onRename, onMove, onDelete, triggerClassName, triggerSize = "w-4 h-4", open, onOpenChange }) {
  return (
    <DropdownMenu open={open} onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild>
        <button
          data-testid={`project-menu-${p.project_id}`}
          onClick={(e) => e.stopPropagation()}
          aria-label={t("dashboard.newProjectMore")}
          className={triggerClassName}
        >
          <MoreHorizontal className={triggerSize} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" title={p.name} onClick={(e) => e.stopPropagation()}>
        <DropdownMenuItem
          data-testid={`toggle-star-${p.project_id}`}
          onClick={() => onToggleStar(p.project_id)}
        >
          <Star className={`w-4 h-4 mr-2 ${p.is_starred ? "fill-[#E05236] text-[#E05236]" : ""}`} />
          {p.is_starred ? t("dashboard.starRemove") : t("dashboard.starAdd")}
        </DropdownMenuItem>
        <DropdownMenuItem
          data-testid={`rename-project-${p.project_id}`}
          onClick={() => onRename(p)}
        >
          <Pencil className="w-4 h-4 mr-2" />
          {t("dashboard.rename")}
        </DropdownMenuItem>
        <DropdownMenuSub>
          <DropdownMenuSubTrigger
            data-testid={`move-project-${p.project_id}`}
          >
            <FolderInput className="w-4 h-4 mr-2" />
            {t("folders.moveTo")}
          </DropdownMenuSubTrigger>
          <DropdownMenuPortal>
            <DropdownMenuSubContent className="rounded-sm">
              <DropdownMenuItem
                data-testid={`move-project-${p.project_id}-unfiled`}
                onClick={() => onMove(p.project_id, null)}
                disabled={!p.folder_id}
              >
                <Inbox className="w-4 h-4 mr-2" />
                {t("folders.unfiled")}
              </DropdownMenuItem>
              {folders.length > 0 && <DropdownMenuSeparator />}
              {folders.map((f) => (
                <DropdownMenuItem
                  key={f.folder_id}
                  data-testid={`move-project-${p.project_id}-to-${f.folder_id}`}
                  onClick={() => onMove(p.project_id, f.folder_id)}
                  disabled={p.folder_id === f.folder_id}
                >
                  <span
                    className="w-2.5 h-2.5 rounded-full mr-2 shrink-0"
                    style={{ background: f.color }}
                  />
                  <span className="truncate">{f.name}</span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuSubContent>
          </DropdownMenuPortal>
        </DropdownMenuSub>
        <DropdownMenuItem
          data-testid={`delete-project-${p.project_id}`}
          onClick={() => onDelete(p.project_id)}
          className="text-red-600"
        >
          <Trash2 className="w-4 h-4 mr-2" />
          {t("common.delete")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// Horizontally scrolling folder filter (replaces the desktop sidebar on phones).
function FolderChips({ folders, projects, activeKey, onSelect, onCreate, t }) {
  const chips = [
    { key: "all", label: t("dashboard.filterAll"), icon: Layers, count: projects.length },
    { key: "starred", label: t("folders.starred"), icon: Star, count: projects.filter((p) => p.is_starred).length },
    { key: "unfiled", label: t("folders.unfiled"), icon: Inbox, count: projects.filter((p) => !p.folder_id).length },
    ...folders.map((f) => ({
      key: f.folder_id,
      label: f.name,
      color: f.color,
      count: projects.filter((p) => p.folder_id === f.folder_id).length,
    })),
  ];
  return (
    <div
      data-testid="folder-chips"
      data-no-ptr
      className="no-scrollbar -mx-4 mb-4 flex scroll-pl-4 snap-x gap-2 overflow-x-auto px-4 pb-1"
    >
      {chips.map((c) => {
        const active = activeKey === c.key;
        return (
          <button
            key={c.key}
            type="button"
            data-testid={`folder-item-${c.key}`}
            onClick={() => onSelect(c.key)}
            className={`flex h-9 shrink-0 snap-start items-center gap-1.5 rounded-full border px-3.5 text-[13px] font-semibold transition-colors ${
              active
                ? "border-neutral-950 bg-neutral-950 text-white"
                : "border-stone-200 bg-white text-neutral-700"
            }`}
          >
            {c.color ? (
              <span className="h-2 w-2 rounded-full" style={{ background: c.color }} />
            ) : c.icon ? (
              <c.icon className="h-3.5 w-3.5" strokeWidth={2} />
            ) : null}
            <span className="max-w-[9rem] truncate">{c.label}</span>
            <span className={`text-[11px] tabular-nums ${active ? "text-white/60" : "text-neutral-400"}`}>
              {c.count}
            </span>
          </button>
        );
      })}
      <button
        type="button"
        data-testid="folder-new-btn"
        onClick={onCreate}
        aria-label={t("folders.new")}
        className="flex h-9 shrink-0 items-center gap-1 rounded-full border border-dashed border-stone-300 px-3 text-[13px] font-semibold text-neutral-500"
      >
        <Plus className="h-3.5 w-3.5" strokeWidth={2.25} />
        {t("folders.new")}
      </button>
    </div>
  );
}

function ProjectRowsSkeleton() {
  return (
    <div data-testid="projects-loading" className="divide-y divide-stone-100">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="flex items-center gap-3 py-3.5">
          <Skeleton className="h-12 w-12 rounded-[14px]" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-3 w-5/6" />
          </div>
        </div>
      ))}
    </div>
  );
}

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [projects, setProjects] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [renaming, setRenaming] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", color: "#E05236" });
  // activeKey: 'all' | 'starred' | 'unfiled' | folder_id
  const [activeKey, setActiveKey] = useState("all");
  const [folderModal, setFolderModal] = useState({ open: false, folder: null });
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [newSheetOpen, setNewSheetOpen] = useState(false);
  const isMobile = useIsMobile();
  const press = useLongPress();
  const [menuFor, setMenuFor] = useState(null); // project whose action sheet was opened by a long-press
  const [searchParams, setSearchParams] = useSearchParams();

  usePageMeta({
    title: `${t("dashboard.title")} — FlowGantt`,
    description: t("seo.dashboardDesc"),
    noindex: true,
  });

  const handleToggleStar = async (project_id, e) => {
    if (e) e.stopPropagation();
    // Optimistic update + client-side re-sort (starred first, then newest first)
    const applySort = (ps) => {
      const copy = [...ps];
      copy.sort((a, b) => {
        const sa = a.is_starred ? 0 : 1;
        const sb = b.is_starred ? 0 : 1;
        if (sa !== sb) return sa - sb;
        return new Date(b.created_at) - new Date(a.created_at);
      });
      return copy;
    };
    setProjects((ps) =>
      applySort(
        ps.map((p) =>
          p.project_id === project_id ? { ...p, is_starred: !p.is_starred } : p,
        ),
      ),
    );
    try {
      await api.post(`/projects/${project_id}/star`);
    } catch (_) {
      // Rollback on error
      setProjects((ps) =>
        applySort(
          ps.map((p) =>
            p.project_id === project_id ? { ...p, is_starred: !p.is_starred } : p,
          ),
        ),
      );
      toast.error(t("common.error"));
    }
  };

  const load = useCallback(async () => {
    try {
      const [proj, fol] = await Promise.all([
        api.get("/projects"),
        api.get("/folders"),
      ]);
      setProjects(proj.data);
      setFolders(fol.data);
    } catch (e) {
      toast.error(t("dashboard.toastLoadError"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const handleMoveProject = async (project_id, folder_id) => {
    // folder_id = null → unfiled ; string → move to folder
    setProjects((ps) =>
      ps.map((p) => (p.project_id === project_id ? { ...p, folder_id } : p)),
    );
    try {
      await api.patch(`/projects/${project_id}`, { folder_id });
      const target = folder_id
        ? folders.find((f) => f.folder_id === folder_id)?.name
        : t("folders.unfiled");
      toast.success(t("folders.moved", { name: target }));
    } catch (_) {
      toast.error(t("common.error"));
      load();
    }
  };

  const handleFolderSaved = (folder) => {
    setFolders((fs) => {
      const idx = fs.findIndex((x) => x.folder_id === folder.folder_id);
      if (idx >= 0) {
        const copy = [...fs];
        copy[idx] = folder;
        return copy;
      }
      return [...fs, folder];
    });
  };

  const handleFolderDeleted = (folder_id) => {
    setFolders((fs) => fs.filter((f) => f.folder_id !== folder_id));
    // Projects previously in that folder become unfiled
    setProjects((ps) =>
      ps.map((p) => (p.folder_id === folder_id ? { ...p, folder_id: null } : p)),
    );
    if (activeKey === folder_id) setActiveKey("all");
  };

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      // Auto-file into currently active folder when one is selected
      const payload = { ...form };
      if (activeKey && activeKey !== "all" && activeKey !== "starred" && activeKey !== "unfiled") {
        payload.folder_id = activeKey;
      }
      const { data } = await api.post("/projects", payload);
      setProjects((p) => [data, ...p]);
      setOpen(false);
      setForm({ name: "", description: "", color: "#E05236" });
      toast.success(t("dashboard.toastCreated"));
      navigate(`/project/${data.project_id}`);
    } catch (e) {
      if (e.response?.status === 402) {
        // The global paywall listener already surfaces the modal via the axios
        // interceptor. Just close the create-project dialog silently.
        setOpen(false);
      } else {
        toast.error(t("dashboard.toastCreateError"));
      }
    }
  };

  const handleDelete = async (project_id) => {
    if (!(await confirmAction({ message: t("dashboard.confirmDeleteProject"), destructive: true }))) return;
    try {
      await api.delete(`/projects/${project_id}`);
      setProjects((p) => p.filter((x) => x.project_id !== project_id));
      toast.success(t("dashboard.toastDeleted"));
    } catch (e) {
      toast.error(t("common.error"));
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  // ── Derived view state (shared by the desktop and phone layouts) ──────────
  let filtered = projects;
  if (activeKey === "starred") filtered = projects.filter((p) => p.is_starred);
  else if (activeKey === "unfiled") filtered = projects.filter((p) => !p.folder_id);
  else if (activeKey !== "all") filtered = projects.filter((p) => p.folder_id === activeKey);
  const activeFolder = folders.find((f) => f.folder_id === activeKey);
  const heading =
    activeKey === "all"
      ? t("dashboard.title")
      : activeKey === "starred"
        ? t("folders.starred")
        : activeKey === "unfiled"
          ? t("folders.unfiled")
          : activeFolder?.name || t("dashboard.title");

  const { pull, refreshing, threshold } = usePullToRefresh(load, { enabled: isMobile });

  // PWA shortcut "Nouveau projet" (/dashboard?new=1)
  useEffect(() => {
    if (searchParams.get("new") === "1") {
      setOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete("new");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const createDialog = (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="rounded-sm max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-black tracking-tight">
            {t("dashboard.newProject")}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleCreate} className="space-y-4 pt-2">
          <div>
            <Label htmlFor="p-name" className="text-xs uppercase tracking-[0.15em]">
              {t("dashboard.projectName")}
            </Label>
            <Input
              id="p-name"
              data-testid="project-name-input"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              className="mt-2 h-11 rounded-sm"
            />
          </div>
          <div>
            <Label htmlFor="p-desc" className="text-xs uppercase tracking-[0.15em]">
              {t("dashboard.description")}
            </Label>
            <Textarea
              id="p-desc"
              data-testid="project-desc-input"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={3}
              className="mt-2 rounded-sm"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.15em]">{t("dashboard.color")}</Label>
            <div className="flex gap-2 mt-2">
              {PROJECT_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  data-testid={`color-${c}`}
                  onClick={() => setForm({ ...form, color: c })}
                  className={`w-10 h-10 md:w-8 md:h-8 rounded-sm transition-transform ${form.color === c ? "ring-2 ring-offset-2 ring-neutral-950 scale-110" : ""}`}
                  style={{ background: c }}
                />
              ))}
            </div>
          </div>
          <DialogFooter className="pt-2">
            <Button
              type="submit"
              data-testid="create-project-submit"
              className="rounded-sm bg-[#E05236] hover:bg-[#C94A31] text-white"
            >
              {t("dashboard.createProject")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );

  const sharedModals = (
    <>
    <UpgradeModal open={upgradeOpen} onOpenChange={setUpgradeOpen} />
    <OnboardingModal open={!!user && user.onboarding_completed === false} />
    <FolderModal
      open={folderModal.open}
      onOpenChange={(v) => setFolderModal((s) => ({ ...s, open: v }))}
      folder={folderModal.folder}
      onSaved={handleFolderSaved}
      onDeleted={handleFolderDeleted}
    />
    <TemplateGalleryModal
      open={galleryOpen}
      onOpenChange={setGalleryOpen}
      onProjectCreated={(proj) => {
        setProjects((ps) => [proj, ...ps]);
        navigate(`/project/${proj.project_id}`);
      }}
    />
    <RenameProjectModal
      open={!!renaming}
      onOpenChange={(v) => !v && setRenaming(null)}
      project={renaming}
      onSave={async (payload) => {
        try {
          const { data } = await api.patch(`/projects/${renaming.project_id}`, payload);
          setProjects((ps) => ps.map((x) => (x.project_id === data.project_id ? data : x)));
          setRenaming(null);
          toast.success(t("dashboard.toastUpdated"));
        } catch (e) {
          toast.error(t("common.error"));
        }
      }}
    />
    </>
  );

  // ── Phone layout ──────────────────────────────────────────────────────────
  if (isMobile) {
    return (
      <div className="min-h-screen bg-white" data-testid="dashboard-mobile">
        <PullIndicator pull={pull} refreshing={refreshing} threshold={threshold} topOffset={52} />
        <header className="sticky top-0 z-30 border-b border-stone-200 bg-white/90 pt-safe backdrop-blur-xl">
          <div className="flex h-[52px] items-center justify-between px-4">
            <div className="flex min-w-0 items-center gap-2">
              <BrandLogo className="h-7 w-7" />
              <span className="truncate font-display text-base font-black tracking-tighter">FLOWGANTT</span>
            </div>
            {user?.is_pro ? (
              <span
                data-testid="pro-badge"
                className="inline-flex items-center gap-1 rounded-full border border-[#E05236] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.15em] text-[#E05236]"
              >
                <Sparkles className="h-3 w-3" />
                Pro
              </span>
            ) : (
              <button
                data-testid="upgrade-header-btn"
                onClick={() => setUpgradeOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-full border border-neutral-950 px-3 py-1.5 text-xs font-semibold"
              >
                <Sparkles className="h-3 w-3" />
                {t("dashboard.upgradeToPro")}
              </button>
            )}
          </div>
        </header>

        <main className="px-4 pb-8 pt-5">
          <InstallPrompt />
          <div className="mb-4 flex items-end justify-between gap-3">
            <div className="min-w-0">
              <h1 className="break-words font-display text-[32px] font-black leading-none tracking-tighter">
                {heading}
              </h1>
              <p className="mt-2 text-sm text-neutral-500">
                {t("dashboard.projectCount", { count: projects.length })}
                {!user?.is_pro && (
                  <> · <span className="text-neutral-400">{t("dashboard.freeLimit", { count: projects.length, limit: FREE_LIMIT })}</span></>
                )}
              </p>
            </div>
            {activeFolder && (
              <button
                type="button"
                data-testid="folder-edit-active"
                aria-label={t("folders.editTitle")}
                onClick={() => setFolderModal({ open: true, folder: activeFolder })}
                className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-stone-100 text-neutral-700"
              >
                <Pencil className="h-4 w-4" />
              </button>
            )}
          </div>

          <FolderChips
            folders={folders}
            projects={projects}
            activeKey={activeKey}
            onSelect={setActiveKey}
            onCreate={() => setFolderModal({ open: true, folder: null })}
            t={t}
          />

          {loading ? (
            <ProjectRowsSkeleton />
          ) : projects.length === 0 ? (
            <div data-testid="empty-projects" className="flex flex-col items-center px-4 py-14 text-center">
              <div className="mb-5 grid h-16 w-16 place-items-center rounded-2xl bg-stone-100">
                <FolderKanban className="h-7 w-7 text-neutral-400" strokeWidth={1.5} />
              </div>
              <div className="mb-1.5 font-display text-xl font-bold">{t("dashboard.empty")}</div>
              <p className="mb-6 max-w-xs text-sm text-neutral-500">{t("dashboard.emptyDesc")}</p>
              <Button
                onClick={() => setOpen(true)}
                data-testid="empty-create-btn"
                className="h-12 w-full max-w-xs rounded-xl bg-neutral-950 text-white"
              >
                {t("dashboard.emptyCta")}
              </Button>
              <Button
                variant="outline"
                onClick={() => setGalleryOpen(true)}
                data-testid="empty-template-btn"
                className="mt-2.5 h-12 w-full max-w-xs rounded-xl border-stone-300"
              >
                <LayoutTemplate className="mr-2 h-4 w-4" />
                {t("dashboard.fromTemplate")}
              </Button>
            </div>
          ) : filtered.length === 0 ? (
            <div data-testid="empty-filter" className="px-4 py-14 text-center text-sm text-neutral-500">
              {t("folders.emptyView")}
            </div>
          ) : (
            <ul className="divide-y divide-stone-100">
              {filtered.map((p) => (
                <li key={p.project_id}>
                  <div
                    data-testid={`project-card-${p.project_id}`}
                    role="link"
                    tabIndex={0}
                    onClick={() => {
                      if (press.consumeClick()) return; // the long-press already opened the menu
                      navigate(`/project/${p.project_id}`);
                    }}
                    onKeyDown={(e) => e.key === "Enter" && navigate(`/project/${p.project_id}`)}
                    {...press.bind(() => setMenuFor(p.project_id))}
                    className="pressable -mx-2 flex select-none items-center gap-3 rounded-2xl px-2 py-3 [-webkit-touch-callout:none]"
                  >
                    <ProjectTile color={p.color} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <span className="truncate font-display text-[17px] font-bold leading-tight tracking-tight">
                          {p.name}
                        </span>
                        {p.is_starred && (
                          <Star className="h-3.5 w-3.5 shrink-0 fill-[#E05236] text-[#E05236]" data-testid={`star-project-${p.project_id}`} />
                        )}
                      </div>
                      <p className="mt-0.5 truncate text-[13px] text-neutral-500">
                        {p.description || t("dashboard.noDescription")}
                      </p>
                      <p className="mt-1 text-[11px] text-neutral-400">
                        {new Date(p.created_at).toLocaleDateString(i18n.language, { day: "numeric", month: "short", year: "numeric" })}
                      </p>
                    </div>
                    <ProjectMenu
                      p={p}
                      folders={folders}
                      t={t}
                      onToggleStar={handleToggleStar}
                      onRename={setRenaming}
                      onMove={handleMoveProject}
                      onDelete={handleDelete}
                      open={menuFor === p.project_id}
                      onOpenChange={(v) => setMenuFor(v ? p.project_id : null)}
                      triggerSize="h-5 w-5"
                      triggerClassName="grid h-11 w-11 shrink-0 place-items-center rounded-full text-neutral-500 active:bg-stone-100"
                    />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </main>

        <Fab
          icon={Plus}
          label={t("dashboard.newProject")}
          testId="new-project-btn"
          aboveTabBar
          onClick={() => setNewSheetOpen(true)}
        />
        <ActionSheet
          open={newSheetOpen}
          onOpenChange={setNewSheetOpen}
          title={t("dashboard.newProject")}
          testId="new-project-sheet"
        >
          <ActionRow
            icon={Plus}
            testId="new-from-scratch"
            onSelect={() => {
              setNewSheetOpen(false);
              setOpen(true);
            }}
          >
            {t("dashboard.fromScratch")}
          </ActionRow>
          <ActionRow
            icon={LayoutTemplate}
            testId="new-from-template"
            onSelect={() => {
              setNewSheetOpen(false);
              setGalleryOpen(true);
            }}
          >
            {t("dashboard.fromTemplate")}
          </ActionRow>
        </ActionSheet>
        {createDialog}
        {sharedModals}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-stone-200 bg-white/80 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <BrandLogo className="w-7 h-7" />
            <span className="font-display font-black text-base sm:text-lg tracking-tighter truncate">
              FLOWGANTT
            </span>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            <LanguageSwitcher />
            <AiCreditBadge onClick={() => setProfileOpen(true)} className="hidden sm:inline-flex" />
            {user?.is_pro ? (
              <span
                data-testid="pro-badge"
                className="hidden sm:inline-flex items-center gap-1.5 border border-[#E05236] text-[#E05236] px-2.5 py-1 rounded-sm text-[10px] uppercase tracking-[0.2em] font-semibold"
              >
                <Sparkles className="w-3 h-3" />
                Pro
              </span>
            ) : (
              <button
                data-testid="upgrade-header-btn"
                onClick={() => setUpgradeOpen(true)}
                className="hidden sm:inline-flex items-center gap-1.5 border border-neutral-950 text-neutral-950 hover:bg-neutral-950 hover:text-white px-3 py-1.5 rounded-sm text-xs uppercase tracking-[0.15em] font-semibold transition-colors"
              >
                <Sparkles className="w-3 h-3" />
                {t("dashboard.upgradeToPro")}
              </button>
            )}
            <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="user-menu-trigger"
                className="flex items-center gap-2 px-2 sm:px-3 py-1.5 rounded-sm hover:bg-stone-100 transition-colors"
              >
                {user?.picture ? (
                  <img
                    src={user.picture}
                    alt={user.name}
                    className="w-7 h-7 rounded-full"
                  />
                ) : (
                  <div className="w-7 h-7 rounded-full bg-neutral-950 text-white grid place-items-center text-xs font-semibold">
                    {user?.name?.[0]?.toUpperCase() || "?"}
                  </div>
                )}
                <span className="hidden sm:inline text-sm font-medium">{user?.name}</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-sm">
              <DropdownMenuItem disabled className="text-xs text-neutral-500">
                {user?.email}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {user?.is_admin && (
                <DropdownMenuItem
                  data-testid="admin-menu-btn"
                  onClick={() => navigate("/admin")}
                  className="text-sm"
                >
                  <Shield className="w-4 h-4 mr-2" />
                  {t("nav.admin")}
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                data-testid="team-menu-btn"
                onClick={() => navigate("/team")}
                className="text-sm"
              >
                <Users className="w-4 h-4 mr-2" />
                {t("nav.team")}
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="support-menu-btn"
                onClick={() => navigate("/support")}
                className="text-sm"
              >
                <LifeBuoy className="w-4 h-4 mr-2" />
                {t("nav.support")}
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="profile-menu-btn"
                onClick={() => setProfileOpen(true)}
                className="text-sm"
              >
                <UserCog className="w-4 h-4 mr-2" />
                {t("nav.profile")}
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="logout-btn"
                onClick={handleLogout}
                className="text-sm"
              >
                <LogOut className="w-4 h-4 mr-2" />
                {t("nav.logout")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
        <div className="flex gap-6 lg:gap-10">
          <FoldersSidebar
            folders={folders}
            projects={projects}
            activeKey={activeKey}
            onSelect={setActiveKey}
            onCreate={() => setFolderModal({ open: true, folder: null })}
            onEdit={(f) => setFolderModal({ open: true, folder: f })}
            onDropProject={handleMoveProject}
          />
          <div className="flex-1 min-w-0 py-8 sm:py-12">
        <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-4 sm:gap-6 mb-8 sm:mb-12">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#E05236] font-semibold mb-3">
              {t("dashboard.sectionLabel")}
            </div>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter break-words">
              {activeKey === "all"
                ? t("dashboard.title")
                : activeKey === "starred"
                  ? t("folders.starred")
                  : activeKey === "unfiled"
                    ? t("folders.unfiled")
                    : folders.find((f) => f.folder_id === activeKey)?.name || t("dashboard.title")}
            </h1>
            <p className="text-neutral-600 mt-3 text-sm">
              {t("dashboard.projectCount", { count: projects.length })}
              {!user?.is_pro && (
                <> · <span className="text-neutral-400">{t("dashboard.freeLimit", { count: projects.length, limit: FREE_LIMIT })}</span></>
              )}
            </p>
          </div>

          <div className="flex items-stretch shrink-0">
            <Button
              data-testid="new-project-btn"
              onClick={() => setOpen(true)}
              className="rounded-sm rounded-r-none bg-neutral-950 hover:bg-neutral-800 text-white h-11 px-5 border-r border-neutral-800"
            >
              <Plus className="w-4 h-4 mr-2" />
              {t("dashboard.newProject")}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="new-project-menu-trigger"
                  className="bg-neutral-950 hover:bg-neutral-800 text-white h-11 px-2.5 rounded-sm rounded-l-none grid place-items-center"
                  aria-label={t("dashboard.newProjectMore")}
                >
                  <ChevronDown className="w-4 h-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-sm">
                <DropdownMenuItem
                  data-testid="new-from-scratch"
                  onClick={() => setOpen(true)}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {t("dashboard.fromScratch")}
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="new-from-template"
                  onClick={() => setGalleryOpen(true)}
                >
                  <LayoutTemplate className="w-4 h-4 mr-2" />
                  {t("dashboard.fromTemplate")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {createDialog}
        </div>

        {loading ? (
          <div
            data-testid="projects-loading"
            className="text-xs uppercase tracking-[0.3em] text-neutral-400"
          >
            {t("common.loading")}
          </div>
        ) : projects.length === 0 ? (
          <div
            data-testid="empty-projects"
            className="border border-dashed border-stone-300 rounded-sm p-16 text-center"
          >
            <FolderKanban className="w-8 h-8 mx-auto text-neutral-400 mb-4" strokeWidth={1.5} />
            <div className="font-display text-xl font-bold mb-2">{t("dashboard.empty")}</div>
            <p className="text-sm text-neutral-600 mb-6">
              {t("dashboard.emptyDesc")}
            </p>
            <div className="flex items-center justify-center gap-3">
              <Button
                onClick={() => setOpen(true)}
                data-testid="empty-create-btn"
                className="rounded-sm bg-neutral-950 text-white hover:bg-neutral-800"
              >
                {t("dashboard.emptyCta")}
              </Button>
              <Button
                variant="outline"
                onClick={() => setGalleryOpen(true)}
                data-testid="empty-template-btn"
                className="rounded-sm border-stone-300"
              >
                <LayoutTemplate className="w-4 h-4 mr-2" />
                {t("dashboard.fromTemplate")}
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {(() => {
                let filtered = projects;
                if (activeKey === "starred") filtered = projects.filter((p) => p.is_starred);
                else if (activeKey === "unfiled") filtered = projects.filter((p) => !p.folder_id);
                else if (activeKey !== "all") filtered = projects.filter((p) => p.folder_id === activeKey);
                if (filtered.length === 0) {
                  return (
                    <div
                      data-testid="empty-filter"
                      className="col-span-full border border-dashed border-stone-300 rounded-sm p-12 text-center text-sm text-neutral-500"
                    >
                      {t("folders.emptyView")}
                    </div>
                  );
                }
                return filtered.map((p) => (
                <div
                  key={p.project_id}
                  data-testid={`project-card-${p.project_id}`}
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.setData(DRAG_TYPE, p.project_id);
                    e.dataTransfer.effectAllowed = "move";
                  }}
                  className={`group border rounded-sm p-6 hover:shadow-sm transition-shadow cursor-pointer relative ${
                    p.is_starred ? "border-[#E05236]/40 bg-orange-50/20" : "border-stone-200"
                  }`}
                  onClick={() => navigate(`/project/${p.project_id}`)}
                >
                  <div className="flex items-start justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-10 h-10 rounded-sm"
                        style={{ background: p.color }}
                      />
                      <button
                        data-testid={`star-project-${p.project_id}`}
                        onClick={(e) => handleToggleStar(p.project_id, e)}
                        title={p.is_starred ? t("dashboard.starRemove") : t("dashboard.starAdd")}
                        className={`p-1.5 rounded-sm hover:bg-stone-100 transition-colors ${
                          p.is_starred
                            ? "text-[#E05236] opacity-100"
                            : "text-neutral-300 hover:text-[#E05236] opacity-0 group-hover:opacity-100"
                        }`}
                      >
                        <Star
                          className={`w-4 h-4 ${p.is_starred ? "fill-current" : ""}`}
                          strokeWidth={2}
                        />
                      </button>
                    </div>
                    <ProjectMenu
                      p={p}
                      folders={folders}
                      t={t}
                      onToggleStar={handleToggleStar}
                      onRename={setRenaming}
                      onMove={handleMoveProject}
                      onDelete={handleDelete}
                      triggerClassName="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-stone-100 rounded-sm"
                    />
                  </div>
                  <div className="font-display font-bold text-xl mb-2 tracking-tight">
                    {p.name}
                  </div>
                  <p className="text-sm text-neutral-600 line-clamp-2 min-h-[2.5rem]">
                    {p.description || t("dashboard.noDescription")}
                  </p>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-400 mt-6">
                    {t("dashboard.createdOn")} {new Date(p.created_at).toLocaleDateString(i18n.language)}
                  </div>
                </div>
              ));
              })()}
            </div>
          </>
        )}
          </div>
        </div>
      </main>
      {sharedModals}
      <ProfileModal
        open={profileOpen}
        onOpenChange={setProfileOpen}
        onUpgradeClick={() => setUpgradeOpen(true)}
      />
    </div>
  );
}
